import { useUIConfiguration } from "@/contexts/UIConfiguration";
import { embeddedScaffold } from "@/scaffold";
import { type api, useAPI } from "@/services/api";
import { lightSandbox } from "@/services/lightSandbox";
import { useAnalytics } from "@Light/app/analytics";
import { getFingerprint } from "@Light/app/fingerprint";
import { Provider as LightProvider } from "@Light/scaffold/Provider";
import { LightSandboxContext } from "@Light/services/lightSandbox";
import type { QueryHookResult } from "@Light/services/queryTypes";
import { createContext, useContext, useEffect } from "react";
import { HeaderLogo } from "./HeaderLogo";

export type AccountState = QueryHookResult<
  typeof api.endpoints.getConfiguration.Types.QueryDefinition
>;

export const ConfigurationContext = createContext<AccountState | undefined>(
  undefined,
);

export type WithConfigurationProps = {
  children: React.ReactNode;
};

export const WithConfiguration: React.FC<WithConfigurationProps> = ({
  children,
}: WithConfigurationProps) => {
  const { useGetConfigurationQuery } = useAPI();
  const configuration = useGetConfigurationQuery(getFingerprint());
  const { setFeatureFlags } = useAnalytics();
  const { hideCloseButton } = useUIConfiguration();

  useEffect(() => {
    if (!configuration.isLoading && configuration.data?.feature_flags) {
      setFeatureFlags(configuration.data.feature_flags);
    }
  }, [
    configuration.data?.feature_flags,
    configuration.isLoading,
    setFeatureFlags,
  ]);

  if (configuration.isLoading) {
    return null;
  }

  children = (
    <ConfigurationContext.Provider value={configuration}>
      {children}
    </ConfigurationContext.Provider>
  );
  if (!configuration.data) {
    return children;
  }

  const featureFlags = configuration.data.feature_flags;

  const allowSupportCallbackWhenAvailable =
    featureFlags?.includes("CUSTOMER_CLIENT_SUPPORT_CALLBACK_AVAILABILITY") ??
    false;

  const headerLogo = configuration.data?.logo ? HeaderLogo : undefined;
  const scaffold = {
    ...embeddedScaffold,
    page: {
      ...embeddedScaffold.page,
      companyName: configuration.data.company_name,
      brandMode: configuration.data.brand_mode,
      legalEntityName: configuration.data.legal_entity_name,
      termsOfServiceUrl: configuration.data.terms_of_service_url ?? undefined,
      goToDashboardOnExit: hideCloseButton,
      supportEmail: configuration.data.support_email,
      supportPhone: configuration.data.support_phone,
      supportHours: configuration.data.support_hours ?? "",
      header: {
        ...embeddedScaffold.page.header,
        headerLogo,
      },
    },
    enroll: {
      ...embeddedScaffold.enroll,
      allowSupportCallbackWhenAvailable,
    },
  };
  return (
    <LightProvider scaffold={scaffold}>
      <LightSandboxContext.Provider value={lightSandbox}>
        {children}
      </LightSandboxContext.Provider>
    </LightProvider>
  );
};

export function useConfiguration() {
  const configuration = useContext(ConfigurationContext);
  if (!configuration?.isSuccess || configuration.error) {
    return undefined;
  }

  return configuration.data;
}

export function useRequireConfiguration() {
  const configuration = useConfiguration();
  if (!configuration) {
    throw new Error(
      "configuration required for use with useRequireConfiguration",
    );
  }

  return configuration;
}
